import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { login } from '../utils/api';
import toast from 'react-hot-toast';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { isSalesRole } from '../utils/roles';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { loginSuccess, admin } = useAuth();
  const navigate = useNavigate();

  const homeFor = (role) => isSalesRole(role) ? '/associate' : '/admin';

  if (admin) { navigate(homeFor(admin.role), { replace: true }); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error('Please fill all fields');
    setLoading(true);
    try {
      const data = await login(email.trim(), password);
      loginSuccess(data.token, data.admin);
      toast.success(`Welcome back, ${data.admin.name}!`);
      navigate(homeFor(data.admin.role));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg, #1D9E75 0%, #0F6E56 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      {/* Background pattern */}
      <div style={{ position: 'fixed', inset: 0, opacity: 0.07, backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 0), radial-gradient(circle at 75% 75%, white 2px, transparent 0)', backgroundSize: '40px 40px' }} />

      <div style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 420,
        boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden', position: 'relative'
      }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #1D9E75, #0F6E56)', padding: '32px 32px 28px', textAlign: 'center', color: '#fff' }}>
          <div style={{
            width: 60, height: 60, background: 'rgba(255,255,255,0.2)', borderRadius: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 18, margin: '0 auto 16px', border: '2px solid rgba(255,255,255,0.3)'
          }}>SRG</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Admin Portal</h1>
          <p style={{ fontSize: 13, opacity: 0.85 }}>SRG Real Estate Pvt. Ltd.</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email" className="form-input"
              placeholder="admin@srgrealestate.com"
              value={email} onChange={e => setEmail(e.target.value)}
              autoComplete="email" autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'} className="form-input"
                placeholder="Enter your password" value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                style={{ paddingRight: 42, width: '100%' }}
              />
              <button
                type="button" onClick={() => setShowPass(!showPass)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', padding: 2 }}
              >
                {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-green" disabled={loading} style={{ width: '100%', justifyContent: 'center', padding: '12px 20px', fontSize: 15 }}>
            {loading ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : <LogIn size={17} />}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <a href="/" style={{ textAlign: 'center', fontSize: 13, color: 'var(--text3)', textDecoration: 'none' }}>
            ← Back to Inventory Dashboard
          </a>
        </form>
      </div>
    </div>
  );
}
